import type { ReactNode, SVGProps } from 'react'

/**
 * Иконки приложения.
 *
 * Дизайн-система их не поставляет, а иконочный пакет ради двух десятков
 * штрихов тянуть незачем. Все нарисованы в одной сетке 24×24 с обводкой
 * `currentColor`, поэтому наследуют цвет и размер шрифта родителя и
 * встают в кнопки и поля кита без подгонки.
 */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** По умолчанию `1em` — иконка следует за размером текста. */
  size?: number | string
}

function Glyph({ size = '1em', children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const MailIcon = (props: IconProps) => (
  <Glyph {...props}>
    <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
    <path d="m3.5 7 7.4 5.3a2 2 0 0 0 2.2 0L20.5 7" />
  </Glyph>
)

export const LockIcon = (props: IconProps) => (
  <Glyph {...props}>
    <rect x="4" y="10.5" width="16" height="10.5" rx="3" />
    <path d="M8 10.5V7.8a4 4 0 1 1 8 0v2.7" />
  </Glyph>
)

export const UserIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </Glyph>
)

export const AtIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="3.6" />
    <path d="M15.6 8.4v4.8a2.9 2.9 0 0 0 5.4 1.4A9.3 9.3 0 1 0 17.6 19" />
  </Glyph>
)

export const EyeIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.8" />
  </Glyph>
)

export const EyeOffIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M9.9 5.2A9.6 9.6 0 0 1 12 5c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-2.9 3.7" />
    <path d="M6.3 7A17.2 17.2 0 0 0 2.5 11.2S6 17.4 12 17.4a9.4 9.4 0 0 0 3.7-.74" />
    <path d="M10 10.1a2.8 2.8 0 0 0 3.9 3.9" />
    <path d="m4 3.6 16.4 16.4" />
  </Glyph>
)

export const HomeIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M4 10.4 12 4l8 6.4V19a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 19Z" />
    <path d="M9.6 20.6v-6h4.8v6" />
  </Glyph>
)

export const MessageIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M20.5 12.4a7.7 7.7 0 0 1-8.3 7.7 8.6 8.6 0 0 1-3-.7l-5 1.6 1.6-4.5a7.7 7.7 0 0 1-.9-3.7 7.7 7.7 0 0 1 7.8-7.7h.5a7.7 7.7 0 0 1 7.3 7.3Z" />
  </Glyph>
)

export const BellIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M18 9.4a6 6 0 1 0-12 0c0 5.4-2 7-2 7h16s-2-1.6-2-7Z" />
    <path d="M13.7 20a2 2 0 0 1-3.4 0" />
  </Glyph>
)

export const BookmarkIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M6.5 4.5h11a1 1 0 0 1 1 1v14.4l-6.5-4-6.5 4V5.5a1 1 0 0 1 1-1Z" />
  </Glyph>
)

export const SettingsIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="3.1" />
    <path d="M19.6 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a1.9 1.9 0 1 1-3.8 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.2a1.9 1.9 0 1 1 0-3.8h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 2.8-1.1v-.2a1.9 1.9 0 1 1 3.8 0v.2a1.6 1.6 0 0 0 2.7 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.8h.3a1.9 1.9 0 1 1 0 3.8h-.3a1.6 1.6 0 0 0-1.4 1Z" />
  </Glyph>
)

export const SearchIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.3-4.3" />
  </Glyph>
)

export const SunIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </Glyph>
)

export const MoonIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M20.5 14.4A8.6 8.6 0 0 1 9.6 3.5a8.9 8.9 0 1 0 10.9 10.9Z" />
  </Glyph>
)

export const MenuIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Glyph>
)

export const PlusIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
)

export const LogOutIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M15 4.5h2.5A2.5 2.5 0 0 1 20 7v10a2.5 2.5 0 0 1-2.5 2.5H15" />
    <path d="M10 16.5 5.5 12 10 7.5M5.5 12h9.5" />
  </Glyph>
)

export const ArrowLeftIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Glyph>
)

export const ArrowRightIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Glyph>
)

export const CheckIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="m5 12.8 4.4 4.4L19 7.6" />
  </Glyph>
)

export const CompassIcon = (props: IconProps) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m15.4 8.6-2 4.8-4.8 2 2-4.8Z" />
  </Glyph>
)

export const SparkleIcon = (props: IconProps) => (
  <Glyph {...props}>
    <path d="M12 3.2 13.9 9l5.9 1.9-5.9 2L12 18.8 10.1 12.9 4.2 11 10.1 9Z" />
    <path d="M18.6 3.4 19.3 5.4 21.3 6.1 19.3 6.8 18.6 8.8 18 6.8 16 6.1 18 5.4Z" />
  </Glyph>
)

// ─────────────────────────────────────────────────────────────
// Логотипы провайдеров: залитые фигуры, а не обводка,
// поэтому рисуются отдельно от Glyph.
// ─────────────────────────────────────────────────────────────

export const GoogleIcon = ({ size = '1em', ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    <path
      fill="#4285F4"
      d="M21.6 12.23c0-.7-.06-1.37-.18-2.02H12v3.82h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.24c1.89-1.74 2.98-4.3 2.98-7.32Z"
    />
    <path
      fill="#34A853"
      d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.06v2.6A10 10 0 0 0 12 22Z"
    />
    <path fill="#FBBC05" d="M6.41 13.9a6 6 0 0 1 0-3.8V7.5H3.06a10 10 0 0 0 0 9l3.35-2.6Z" />
    <path
      fill="#EA4335"
      d="M12 5.98c1.47 0 2.79.51 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2A10 10 0 0 0 3.06 7.5l3.35 2.6C7.2 7.74 9.4 5.98 12 5.98Z"
    />
  </svg>
)

export const AppleIcon = ({ size = '1em', ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    <path d="M16.36 12.62c-.02-2.24 1.83-3.32 1.91-3.37-1.04-1.52-2.66-1.73-3.24-1.75-1.38-.14-2.69.81-3.39.81-.7 0-1.78-.79-2.93-.77-1.5.02-2.89.87-3.66 2.22-1.56 2.71-.4 6.72 1.12 8.92.74 1.08 1.63 2.29 2.79 2.24 1.12-.04 1.54-.72 2.9-.72 1.35 0 1.74.72 2.92.7 1.21-.02 1.97-1.1 2.71-2.18.85-1.25 1.2-2.46 1.22-2.52-.03-.01-2.34-.9-2.35-3.58ZM14.14 5.6c.61-.75 1.03-1.78.92-2.81-.88.04-1.96.59-2.6 1.33-.57.66-1.07 1.72-.94 2.73.99.08 1.99-.5 2.62-1.25Z" />
  </svg>
)

export const YandexIcon = ({ size = '1em', ...rest }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    <circle cx="12" cy="12" r="10" fill="#FC3F1D" />
    <path
      fill="#fff"
      d="M13.1 18.3h1.9V5.7h-2.8c-2.8 0-4.3 1.44-4.3 3.56 0 1.7.8 2.7 2.24 3.72L7.66 18.3h2.06l2.8-4.18-.98-.66c-1.16-.78-1.72-1.4-1.72-2.7 0-1.14.8-1.92 2.32-1.92h.96Z"
    />
  </svg>
)
